import type { FreshStrawStageGradeEntry } from '@/app/services/freshStrawRest';
import { GradingGridRow } from '@/components/common/grading-grid/types';
import {
  defaultGradingRows,
  gridToStageGradeCount,
} from '@/components/common/grading-grid/utils';

/**
 * Pure conversion helpers between the 19-bucket fresh-straw entry list and the
 * shared <GradingGrid> component's 18-cell (stage 4-9 × grade 1-3) row model
 * plus a dedicated zero-bucket counter.
 *
 * Why split like this: GradingGrid is reused across the nucleus UI and doesn't
 * know about the legacy `@sg00` stage-0/grade-0 bucket. The dialog composes the
 * grid with its own zero input rather than forking GradingGrid, so these
 * helpers round-trip the full 19-bucket shape.
 */

export interface StageGradeDialogState {
  rows: GradingGridRow[];
  zeroBucket: number;
}

const STAGE_FIELDS = ['s4', 's5', 's6', 's7', 's8', 's9'] as const;

/** Build an empty dialog state — all 18 grid cells null + zero bucket 0. */
export const emptyDialogState = (): StageGradeDialogState => ({
  rows: defaultGradingRows.map((r) => ({ ...r })),
  zeroBucket: 0,
});

/** Split a saved entry list into the dialog's grid + zero-bucket shape. */
export function entriesToDialogState(
  entries: FreshStrawStageGradeEntry[] | null | undefined,
): StageGradeDialogState {
  const state = emptyDialogState();
  if (!entries || entries.length === 0) return state;

  for (const entry of entries) {
    if (!entry) continue;
    const { stage, grade, count } = entry;
    if (stage === 0 && grade === 0) {
      state.zeroBucket = (state.zeroBucket || 0) + (count ?? 0);
      continue;
    }

    const row = state.rows.find((r) => r.grade === grade);
    if (!row) continue;

    const fieldName = `s${stage}` as (typeof STAGE_FIELDS)[number];
    if (!STAGE_FIELDS.includes(fieldName)) continue;

    const existing = row[fieldName] ?? 0;
    row[fieldName] = existing + (count ?? 0);
  }

  return state;
}

/**
 * Fold the dialog's grid + zero-bucket back into a canonical entry list the
 * fresh-straw save endpoints accept. Cells with count <= 0 are dropped — the
 * backend already defaults missing buckets to zero in
 * FreshStrawRepository.BuildStageGradeParameters.
 */
export function dialogStateToEntries(
  state: StageGradeDialogState,
): FreshStrawStageGradeEntry[] {
  const entries: FreshStrawStageGradeEntry[] = gridToStageGradeCount(
    state.rows,
  );
  if (state.zeroBucket && state.zeroBucket > 0) {
    entries.push({ stage: 0, grade: 0, count: state.zeroBucket });
  }
  return entries;
}

/** Sum of all 19 cells in the current dialog state. */
export function sumDialogState(state: StageGradeDialogState): number {
  const gridSum = state.rows.reduce(
    (acc, row) =>
      acc +
      STAGE_FIELDS.reduce(
        (rowSum, field) => rowSum + (Number(row[field]) || 0),
        0,
      ),
    0,
  );
  return gridSum + (state.zeroBucket || 0);
}
